import type { PropDef, PropKind, TrackDef, Vec2 } from '../core/types';
import { hash01 } from '../core/math';

/**
 * „Reggeli csúcsforgalom" – belvárosi kör.
 * A vezérlőpontok szándékosan durvák: a Track osztály Catmull-Rom spline-nal simítja.
 *
 * A kör iránya olyan, hogy a haladási irány bal oldala a belső ív, a jobb oldala a külső.
 * Ezért az épületek a jobb (pozitív laterális) oldalra kerülnek, a belső oldal park.
 */
const CENTERLINE: Vec2[] = (() => {
  const x0 = 500;
  const x1 = 2400;
  const y0 = 420;
  const y1 = 1850;
  // sarok sugarak: jobb-alsó, jobb-felső, bal-felső, bal-alsó
  const rBR = 320;
  const rTR = 260;
  const rTL = 285;
  const rBL = 250;
  const pts: Vec2[] = [];

  const straight = (ax: number, ay: number, bx: number, by: number): void => {
    const len = Math.hypot(bx - ax, by - ay);
    const steps = Math.max(2, Math.round(len / 90));
    for (let i = 0; i < steps; i++) {
      const t = i / steps;
      pts.push({ x: ax + (bx - ax) * t, y: ay + (by - ay) * t });
    }
  };

  const arc = (cx: number, cy: number, r: number, a0: number, a1: number): void => {
    const steps = Math.max(4, Math.round((Math.abs(a1 - a0) / (Math.PI / 2)) * 7));
    for (let i = 0; i < steps; i++) {
      const a = a0 + ((a1 - a0) * i) / steps;
      pts.push({ x: cx + Math.cos(a) * r, y: cy + Math.sin(a) * r });
    }
  };

  // alsó egyenes (jobbra)
  straight(x0 + rBL, y1, x1 - rBR, y1);
  // jobb-alsó sarok (90° -> 0°)
  arc(x1 - rBR, y1 - rBR, rBR, Math.PI / 2, 0);
  // jobb oldal (felfelé)
  straight(x1, y1 - rBR, x1, y0 + rTR);
  // jobb-felső sarok (0° -> -90°)
  arc(x1 - rTR, y0 + rTR, rTR, 0, -Math.PI / 2);
  // felső egyenes (balra)
  straight(x1 - rTR, y0, x0 + rTL, y0);
  // bal-felső sarok (-90° -> -180°)
  arc(x0 + rTL, y0 + rTL, rTL, -Math.PI / 2, -Math.PI);
  // bal oldal (lefelé)
  straight(x0, y0 + rTL, x0, y1 - rBL);
  // bal-alsó sarok (180° -> 90°)
  arc(x0 + rBL, y1 - rBL, rBL, Math.PI, Math.PI / 2);

  return pts;
})();

const props: PropDef[] = [];
const add = (kind: PropKind, s: number, lateral: number, scale = 1, variant = 0): void => {
  props.push({ kind, s, lateral, scale, variant });
};

// --- külső ív: házsor ---
for (let i = 0; i < 44; i++) {
  const s = (i + 0.4) / 44;
  const h = hash01(i * 92821 + 17);
  const h2 = hash01(i * 31337 + 5);
  // néha üres telek, hogy ne legyen monoton fal
  if (h > 0.1) add('building', s, 252 + h2 * 92, 0.8 + h * 0.55, Math.floor(h * 6));
  if (h2 > 0.72) add('tree', s + 0.005, 200 + h * 34, 0.75 + h2 * 0.4, Math.floor(h * 3));
  if (h < 0.08) add('kiosk', s + 0.004, 196, 0.9, Math.floor(h2 * 2));
}

// --- belső ív: park, fák, padok ---
const parkZones = (s: number): boolean => (s > 0.1 && s < 0.32) || (s > 0.55 && s < 0.78);
for (let i = 0; i < 76; i++) {
  const s = i / 76;
  const h = hash01(i * 7829 + 3);
  const h2 = hash01(i * 104729 + 11);
  const park = parkZones(s);
  if (park || h > 0.45) add('tree', s, -(168 + h * 44), 0.75 + h2 * 0.55, Math.floor(h2 * 3));
  if (park && h2 > 0.6) add('bench', s + 0.004, -158, 0.9 + h * 0.3, Math.floor(h2 * 2));
  if (!park && h > 0.86) add('kiosk', s + 0.003, -170, 0.85, Math.floor(h2 * 2));
}

// --- utcalámpák mindkét oldalon ---
for (let i = 0; i < 40; i++) {
  const s = i / 40;
  add('lamp', s, i % 2 === 0 ? 176 : -176, 1, i % 3);
}

// --- apró utcabútorok ---
for (let i = 0; i < 30; i++) {
  const s = (i + 0.25) / 30;
  const h = hash01(i * 5501 + 29);
  if (h < 0.3) add('hydrant', s, 168, 1, 0);
  else if (h < 0.6) add('bin', s, -168, 1, 0);
  else if (h < 0.85) add('sign', s, 182, 1, i % 4);
}

// --- zebrák a kanyarok előtt ---
for (const s of [0.075, 0.2, 0.33, 0.47, 0.61, 0.78, 0.92]) add('crosswalk', s, 0, 1, 0);

// --- nyilak az aszfalton ---
for (const s of [0.1, 0.36, 0.7, 0.86]) {
  add('arrow', s, -48, 1, 0);
  add('arrow', s + 0.012, 48, 1, 1);
}

// --- építési terület a szűk utcában ---
for (let i = 0; i < 7; i++) {
  add('cone', 0.635 + i * 0.004, -74 + i * 9, 1, i % 2);
}

// --- parkoló autók, tócsák, villamos ---
add('parkedCar', 0.215, 196, 1, 0);
add('parkedCar', 0.228, 196, 1, 2);
add('parkedCar', 0.835, -196, 1, 1);
add('puddle', 0.415, 42, 1.3, 0);
add('puddle', 0.44, -58, 1, 0);
add('tram', 0.5, 0, 1, 0);

export const CITY_TRACK: TrackDef = {
  id: 'belvaros-reggel',
  name: 'Reggeli csúcsforgalom',
  subtitle: 'Belvárosi kör · 3 kör',
  description:
    'A Nagykörúton indul a mezőny, majd jön a szűk belvárosi S, a villamos-sín, a park-ív és a bal oldali lejtő. Reggel 8-kor mindenki siet – te ne siess a falnak.',
  centerline: CENTERLINE,
  roadHalfWidth: 96,
  kerbWidth: 12,
  sidewalkWidth: 36,
  startS: 0.03,
  laps: 3,
  props,
  boostPads: [
    { s: 0.145, lateral: 0, length: 230, width: 150 },
    { s: 0.515, lateral: 0, length: 230, width: 150 },
  ],
  itemRows: [
    { s: 0.12, lateral: 0, count: 4, spacing: 52 },
    { s: 0.32, lateral: 0, count: 4, spacing: 52 },
    { s: 0.62, lateral: 0, count: 4, spacing: 52 },
    { s: 0.84, lateral: 0, count: 4, spacing: 52 },
  ],
  ambient: {
    sky: '#202838',
    asphalt: '#4b5059',
    asphaltDark: '#3d424b',
    lane: '#e7d6a2',
    kerb: '#c9543f',
    sidewalk: '#8f8c84',
    wall: '#2b3140',
    accent: '#ffc857',
    shadow: 'rgba(10,12,18,0.38)',
  },
  cameraZoom: 1,
};
